import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import WebService from "../Services/WebService";
import { mediaUrl } from "../Services/mediaUrl";
import Paginations from "../Customs/pagination/Paginations";
import { FaSearch, FaEdit, FaTrash } from "react-icons/fa";
import { MdAdd } from "react-icons/md";
import styles from "./RawMaterialCategories.module.css";
import AddCategoryPopup from "./AddCategoryPopup";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import type { RootState } from "../Services/Store";
import type { AccessItem } from "../Models/Models";

export interface RawMaterialCategory {
  category_id: number;
  category_name: string;
  description: string;
  parent_category_id?: number;
  category_img?: string;
}

const PAGE_SIZE = 9;

const RawMaterialCategories: React.FC = () => {
  // --- RBAC
  const accessList = useSelector((state: RootState) => state.auth.access || []);
  const inventoryAccess =
    (accessList.find((a: AccessItem) => a.code === "inventory_management") as AccessItem) || ({} as AccessItem);
  const canRead = !!inventoryAccess.read;
  const canWrite = !!inventoryAccess.write;
  const canUpdate = !!inventoryAccess.update;
  const canDelete = !!inventoryAccess.delete;

  // --- State with URL Params
  const [searchParams, setSearchParams] = useSearchParams();
  const [allCategories, setAllCategories] = useState<RawMaterialCategory[]>([]);
  // const [filteredCategories, setFilteredCategories] = useState<RawMaterialCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // --- Get from params, fallback to ""
  const [search, setSearch] = useState<string>(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState<number>(Number(searchParams.get("page")) || 1);

  const [showAddPanel, setShowAddPanel] = useState<boolean>(false);
  const [editCategory, setEditCategory] = useState<RawMaterialCategory | null>(null);
  const navigate = useNavigate();

  // --- Fetch categories
const fetchCategories = async (q = "") => {
  setLoading(true);
  setError("");
  try {
    // if you always need type=NO_SUB_CATEGORY, keep it here
    const like = q.trim();
    const qs = new URLSearchParams();
    if (like) qs.set("like", like);
    // qs.set("type", "NO_SUB_CATEGORY"); // uncomment if your API requires it
    const action =
      `/v1/door-management/raw-material/categories` + (qs.toString() ? `?${qs}` : "");

    const res: any = await WebService.getAPI({ action });

    let arr: RawMaterialCategory[] = [];
    if (Array.isArray(res?.data)) arr = res.data;
    else if (Array.isArray(res?.data?.data)) arr = res.data.data;
    else if (Array.isArray(res)) arr = res;

    setAllCategories(arr);      // <- now this is already server-filtered
  } catch {
    setError("Failed to fetch categories.");
    setAllCategories([]);
  }
  setLoading(false);
};

// When the URL params change, sync state and refetch from server with ?like=
useEffect(() => {
  const paramSearch = searchParams.get("search") || "";
  const paramPage = Number(searchParams.get("page")) || 1;

  if (paramSearch !== search) setSearch(paramSearch);
  if (paramPage !== currentPage) setCurrentPage(paramPage);

  // Always refetch from server using "like"
  fetchCategories(paramSearch);
  // eslint-disable-next-line
}, [searchParams]);


  // --- Filter logic, tied to URL params
  // useEffect(() => {
  //   // Keep params in sync with state (for browser/share/back-forward support)
  //   const params: Record<string, string> = {};
  //   if (search) params.search = search;
  //   if (currentPage > 1) params.page = String(currentPage);
  //   setSearchParams(params);

  //   // Filtering
  //   const filtered = allCategories.filter((cat) =>
  //     cat.category_name.toLowerCase().includes((search || "").toLowerCase())
  //   );
  //   setFilteredCategories(filtered);

  //   // If new search, reset to page 1
  //   // (only if current page > last page, reset)
  //   const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  //   if (currentPage > totalPages) setCurrentPage(1);
  //   // eslint-disable-next-line
  // }, [search, allCategories, currentPage, setSearchParams]);

  // --- Handle direct param changes (for browser back/forward)
  useEffect(() => {
  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (currentPage > 1) params.page = String(currentPage);
  setSearchParams(params);
  // eslint-disable-next-line
}, [search, currentPage]);
  useEffect(() => {
    const paramPage = Number(searchParams.get("page")) || 1;
    if (paramPage !== currentPage) setCurrentPage(paramPage);

    const paramSearch = searchParams.get("search") || "";
    if (paramSearch !== search) setSearch(paramSearch);
    // eslint-disable-next-line
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setCurrentPage(1);
    // search already updated in input
  };

  const handlePageChange = (page: number): void => {
    setCurrentPage(page);
    // URL param update happens in useEffect above
  };

const paginatedCategories = allCategories.slice(
  (currentPage - 1) * PAGE_SIZE,
  currentPage * PAGE_SIZE
);

  const handleOpenAddPanel = () => {
    if (!canWrite) {
      Swal.fire({
        icon: "error",
        title: "Permission Denied",
        text: "You do not have permission to add categories.",
        confirmButtonColor: "#FFD400",
      });
      return;
    }
    setEditCategory(null);
    setShowAddPanel(true);
  };

  const handleEdit = (cat: RawMaterialCategory): void => {
    if (!canUpdate) {
      Swal.fire({
        icon: "error",
        title: "Permission Denied",
        text: "You do not have permission to edit categories.",
        confirmButtonColor: "#FFD400",
      });
      return;
    }
    setEditCategory(cat);
    setShowAddPanel(true);
  };

  const handleDelete = async (cat: RawMaterialCategory): Promise<void> => {
    if (!canDelete) {
      Swal.fire({
        icon: "error",
        title: "Permission Denied",
        text: "You do not have permission to delete categories.",
        confirmButtonColor: "#FFD400",
      });
      return;
    }
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete Category",
      text: `Delete category "${cat.category_name}"? This cannot be undone.`,
      showCancelButton: true,
      confirmButtonColor: "#e74c3c",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, Delete",
      reverseButtons: true,
    });
    if (!res.isConfirmed) return;
    try {
      await WebService.deleteAPI({
        action: `/v1/door-management/raw-material/categories/${cat.category_id}`,
      });
      await Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Category deleted successfully.",
        confirmButtonColor: "#FFD400",
        timer: 1300,
        showConfirmButton: false,
      });
      fetchCategories();
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Failed to delete category.",
        confirmButtonColor: "#FFD400",
      });
    }
  };

  if (!canRead) {
    Swal.fire({
      icon: "error",
      title: "No Access",
      text: "You do not have access to view Raw Material Categories.",
      confirmButtonColor: "#FFD400",
    });
    return (
      <div style={{ padding: 40, color: "red", fontWeight: 600, fontSize: 22 }}>
        You do not have access to view Raw Material Categories.
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerRow}>
        <div className={styles.pageTitle}>Raw Material Categories</div>
      </div>
      <div className={styles.buttonRow}>
        <button
          className={styles.bigBtn}
          onClick={() => navigate("/raw-material")}
        >
          Raw Material
        </button>
        <button className={`${styles.bigBtn} ${styles.active}`}>
          Raw Material Categories 
        </button>
      </div>

      <div className={styles.tableToolbar}>
        <form className={styles.searchForm} onSubmit={handleSearch}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by Category Name…"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </form>
        <button
          className={styles.addBtn}
          onClick={handleOpenAddPanel}
          disabled={!canWrite}
          style={!canWrite ? { opacity: 0.5, cursor: "not-allowed" } : {}}
        >
          <MdAdd style={{ marginRight: 8, fontSize: "1.35rem" }} />
          Add New Category
        </button>
      </div>

      <div className={styles.tableCard}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "center" }}>Image</th>
              <th className={`${styles.idHeader} ${styles.centerCell}`}>ID</th>
              <th style={{ textAlign: "center" }}>Name</th>
              <th style={{ textAlign: "center" }}>Description</th>
              <th style={{ textAlign: "center" }}>Parent Category</th>
              <th style={{ textAlign: "center" }} className={styles.actionsHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 38, fontSize: 16 }}>
                  Loading categories…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={6} className={styles.errorText} style={{ textAlign: "center", padding: 38 }}>
                  {error}
                </td>
              </tr>
            ) : paginatedCategories.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 38, color: "#666", fontSize: 16 }}>
                  No categories found.
                </td>
              </tr>
            ) : (
              paginatedCategories.map((cat) => (
                <tr key={cat.category_id} className={styles.fullRow}>
                  <td className={styles.imageCell}>
                    <img
                      src={cat.category_img ? mediaUrl(cat.category_img) : "/Assets/no-pictures.png"}
                      alt={cat.category_name}
                      className={styles.categoryImg}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/Assets/no-pictures.png";
                      }}
                    />
                  </td>
                  <td className={styles.centerCell}>{cat.category_id}</td>
                  <td className={styles.centerCell}>{cat.category_name}</td>
                  <td className={styles.centerCell}>{cat.description || "—"}</td>
                  <td className={styles.centerCell}>{cat.parent_category_id ?? "—"}</td>
                  <td className={`${styles.actionsCell} ${styles.centerCell}`}>
                    <div className={styles.actionsInner}>
                      <FaEdit
                        className={styles.editIcon}
                        title="Edit"
                        onClick={() => handleEdit(cat)}
                        tabIndex={0}
                        role="button"
                        aria-label={`Edit category ${cat.category_name}`}
                      />
                      <FaTrash
                        className={styles.deleteIcon}
                        title="Delete"
                        onClick={() => handleDelete(cat)}
                        tabIndex={0}
                        role="button"
                        aria-label={`Delete category ${cat.category_name}`}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

{allCategories.length > PAGE_SIZE && (
  <div className={styles.paginationWrapper}>
    <Paginations
      totalItems={allCategories.length}
      itemsCountPerPage={PAGE_SIZE}
      changePage={handlePageChange}
      changeCurrentPage={currentPage}
    />
  </div>
)}


      {showAddPanel && (
        <AddCategoryPopup
          onClose={() => {
            setShowAddPanel(false);
            setEditCategory(null);
          }}
          onSuccess={() => {
            setShowAddPanel(false);
            setEditCategory(null);
            fetchCategories();
          }}
          parentCategories={allCategories}
          editCategory={editCategory}
          canWrite={canWrite}
          canUpdate={canUpdate}
        />
      )}
    </div>
  );
};

export default RawMaterialCategories;
